import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import {
  ActivityDto,
  ActivityExpandedAuthenticationPayloadDto,
  ArchiveDto,
  AssetDto,
  AssetStatus,
  CommentDto,
  CustomerActivityDto,
  DefaultDisplayGuestDto,
  DisplayUser,
  GalleryDto,
  GalleryStatus,
} from '@picflow/types';
import { EMPTY, Observable, catchError, filter, forkJoin, from, map, of, switchMap, take, tap, throwError } from 'rxjs';
import { ScreenSizes } from '../classes/screen-sizes.class';
import { activityPerPage, biggestGroupTimeDiff } from '../consts';
import { AssetUserFeedbackMap } from '../gallery-shared/interfaces/asset-user-feedback-map';
import { getAssetStatusColor } from '../gallery/pipes/asset-status-color.pipe';
import { getAssetStatusIcon } from '../gallery/pipes/asset-status-icon.pipe';
import { GROWTHBOOK } from '../growthbook';
import {
  ActivityDay,
  ActivityGroup,
  ActivityGroupDay,
  ActivityGroupPerson,
  ConvertedActivity,
  GroupTextItem,
  activityOutputContext,
  activityType,
  getActivityResponse,
  groupingFields,
} from '../interfaces/activity.interface';
import { debounce } from '../lib/debounce';
import { forkJoinSafe } from '../lib/safe-fork-join';
import { splitName } from '../lib/split-name';
import { unique } from '../lib/unique';
import { getGalleryStatusColor } from '../pipes/gallery-status-color.pipe';
import { getGalleryStatusIcon } from '../pipes/gallery-status-icon.pipe';
import { AppConfigService } from '../providers/config.service';
import { convertToGeneralRelativeTime } from '../shared-extra/pipes/relative-time.pipe';
import { AccessLevelService } from './access-level.service';
import { AssetService } from './asset.service';
import { GalleryPasswordService } from './gallery-password.service';
import { GalleryService } from './gallery.service';
import { GetAllService } from './get-all.service';
import { ObservableDataLoaderService } from './observable-data-loader.service';
import { SocketService } from './socket.service';
import { UnseenActivityService } from './unseen-activity.service';

/**
 * 1. Get activity
 * 2. Split activity into days
 * 3. Create groups from all activity in each day
 * 4. Figure out what text should be shown for each group
 */

@Injectable({
  providedIn: 'root',
})
export class ActivityService {
  private config = inject(AppConfigService);
  private http = inject(HttpClient);
  private galleryService = inject(GalleryService);
  private assetService = inject(AssetService);
  private unseenActivityService = inject(UnseenActivityService);
  private socketService = inject(SocketService);
  private galleryPasswordService = inject(GalleryPasswordService);
  private loader = inject(ObservableDataLoaderService);
  private getAllService = inject(GetAllService);
  private accessLevelService = inject(AccessLevelService);
  private growthBook = inject(GROWTHBOOK);

  activityToUserActivityMap: Map<string, string> = new Map();
  // allTypes = '&types[]=comment.created&types[]=gallery.created&types[]=gallery-view.created&types[]=asset-annotation.created&types[]=asset-color-label.created&types[]=asset-download.created&types[]=asset-like.created&types[]=asset-status.created&types[]=asset-version.created&types[]=single-asset-download.created&types[]=referral.created&types[]=referral.updated&types[]=user-tenant.created'

  assetFeedbackTypes = [
    'comment.created',
    'asset-annotation.created',
    'asset-color-label.created',
    'asset-like.created',
    'asset-status.created',
  ]
    .map(type => `&types[]=${type}`)
    .join('');

  assetNegativeFeedbackTypes = ['comment.deleted', 'asset-annotation.deleted', 'asset-like.deleted']
    .map(type => `&types[]=${type}`)
    .join('');

  galleryFeedbackTypes = ['gallery.status-updated'].map(type => `&types[]=${type}`).join('');

  feedbackTypes = [this.assetFeedbackTypes, this.galleryFeedbackTypes].join('');

  otherTypes = ['gallery-view.created', 'archive.created', 'single-asset-download.created']
    .map(type => `&types[]=${type}`)
    .join('');
  allTypes = [...this.feedbackTypes, ...this.otherTypes].join('');

  public newUserActivity$ = this.socketService.onSocketEvent.pipe(
    filter(event => event && event.name === 'user-activity.created')
  );

  readonly onNewUserActivity = this.newUserActivity$.subscribe(event => {
    const rawItem = event.payload.userActivity as CustomerActivityDto;
    rawItem.activity['id'] = rawItem.id;
    this.convertActivity([rawItem.activity as ActivityDto]).subscribe(([item]) => {
      if (!item) return;
      this.unseenActivityService.updateUnseen(item, false);
    });
    return event;
  });

  readonly onNewActivity = this.socketService.onSocketEvent
    .pipe(
      filter(event => !!event?.payload),
      map(event => {
        if (event.name.startsWith('asset')) {
          return (event.payload.asset as AssetDto)?.gallery as string;
        }
        if (event.name.startsWith('comment')) {
          const assetId = (event.payload.comment as CommentDto).asset;
          const galleryOrGalleryId = this.assetService.allAssets.get(assetId)?.gallery;
          if (typeof galleryOrGalleryId === 'string') return galleryOrGalleryId;
          return galleryOrGalleryId?.id;
        }
      })
    )
    .subscribe(galleryId => {
      if (galleryId) this.clearGalleryAssetUserFeedbackMap(galleryId);
    });

  private clearGalleryAssetUserFeedbackMap = debounce((galleryId: string) => {
    this.loader.remove(`galleryAssetUserFeedbackMap_${galleryId}`);
    this.loader.remove(`currentGalleryActivity_${galleryId}`);
  }, 100);

  public getGalleryAssetUserFeedbackMap(gallery: GalleryDto, assets: AssetDto[]): Observable<AssetUserFeedbackMap> {
    return this.loader.get(`galleryAssetUserFeedbackMap_${gallery.id}`, () => {
      return forkJoin([
        this.getCurrentGalleryActivity(gallery).pipe(take(1)),
        from(this.accessLevelService.getAccessLevel(gallery)),
        this.accessLevelService.isGalleryGuest(gallery).pipe(take(1)),
      ]).pipe(
        map(([activity, accessLevel, isGalleryGuest]) => {
          const showPrivateComments = accessLevel === 'edit' || !isGalleryGuest;
          const map: AssetUserFeedbackMap = {};

          assets.forEach(asset => {
            const assetActivity = activity.filter(a => a.asset === asset.id && a.type);

            map[asset.id] = {
              color_label: {
                red: assetActivity.find(a => a.type === 'asset.color_label_red')?.userOrGuestId,
                blue: assetActivity.find(a => a.type === 'asset.color_label_blue')?.userOrGuestId,
                green: assetActivity.find(a => a.type === 'asset.color_label_green')?.userOrGuestId,
                orange: assetActivity.find(a => a.type === 'asset.color_label_orange')?.userOrGuestId,
                purple: assetActivity.find(a => a.type === 'asset.color_label_purple')?.userOrGuestId,
              },
              activeColorLabel: assetActivity.find(a => a.type.startsWith('asset.color_label'))?.userOrGuestId,
              status: {
                approved: assetActivity.find(a => a.type === 'asset.status_approved')?.userOrGuestId,
                rejected: assetActivity.find(a => a.type === 'asset.status_rejected')?.userOrGuestId,
                in_progress: assetActivity.find(a => a.type === 'asset.status_in_progress')?.userOrGuestId,
                needs_review: assetActivity.find(a => a.type === 'asset.status_needs_review')?.userOrGuestId,
              },
              activeStatus: assetActivity.find(a => a.type.startsWith('asset.status'))?.userOrGuestId,
              liked: unique(assetActivity.filter(a => a.type === 'asset.liked').map(a => a.userOrGuestId)),
              commented: unique(
                assetActivity
                  .filter(a => {
                    if (a.type !== 'comment.created') return false;
                    if (a.private && !showPrivateComments) return false;
                    return true;
                  })
                  .map(a => a.userOrGuestId)
              ),
              annotatated: unique(
                assetActivity
                  .filter(a => {
                    if (a.type !== 'asset-annotation.created') return false;
                    if (a.private && !showPrivateComments) return false;
                    return true;
                  })
                  .map(a => a.userOrGuestId)
              ),
            };
          });
          return map;
        })
      );
    });
  }

  public getVersionCreatedActivity(assetId: string, galleryId: string): Observable<ActivityDto> {
    const url = `${this.config.apiEndpoint}/activities?asset=${assetId}&gallery=${galleryId}&types[]=asset-version.created&limit=1${this.getPassword(galleryId)}`;
    return this.http.get<{ data: ActivityDto[] }>(url).pipe(map(resp => resp.data[0]));
  }

  public getActivityForGuest(guestId: string, page: number, limit = activityPerPage): getActivityResponse {
    const url = `${this.config.apiEndpoint}/activities?limit=${limit}&page=${page}&sort[]=-created_at&guest=${guestId}${this.allTypes}`;
    return this.getActivity(url, 'tenant', limit);
  }

  public getActivityForUser(userId: string, page: number, limit = activityPerPage): getActivityResponse {
    const url = `${this.config.apiEndpoint}/activities?limit=${limit}&page=${page}&sort[]=-created_at&user=${userId}${this.allTypes}`;
    return this.getActivity(url, 'tenant', limit);
  }

  private getGalleryActivity(galleryId: string, page: number, limit = activityPerPage): getActivityResponse {
    const url = `${this.config.apiEndpoint}/activities?gallery=${galleryId}&limit=${limit}&page=${page}&sort[]=-created_at${this.allTypes}`;
    return this.getActivity(url, 'gallery', limit);
  }

  private getTenantActivity(page: number, limit = activityPerPage): getActivityResponse {
    const url = `${this.config.apiEndpoint}/activities?limit=${limit}&page=${page}&sort[]=-created_at${this.allTypes}`;
    return this.getActivity(url, 'tenant', limit);
  }

  private getAssetActivity(assetId: string, page: number, limit = activityPerPage): getActivityResponse {
    const url = `${this.config.apiEndpoint}/activities?asset=${assetId}&limit=${limit}&page=${page}&sort[]=-created_at${this.allTypes}`;
    return this.getActivity(url, 'asset', limit);
  }

  public getCurrentGalleryActivity(gallery: GalleryDto) {
    return this.loader.get(`currentGalleryActivity_${gallery.id}`, () => {
      const isCurrent =
        this.growthBook.isOn('is_current') && gallery.created_at * 1000 > new Date(2024, 5, 1).getTime();
      let url = `${this.config.apiEndpoint}/activities?gallery=${gallery.id}${this.assetFeedbackTypes}`;
      if (isCurrent) {
        url += `&is_current=true`;
      } else {
        url += `${this.assetNegativeFeedbackTypes}&sort[]=-created_at`;
      }

      url += this.getPassword(gallery.id);

      return this.getAllService.getAll<ActivityDto>(url, 100, true).pipe(
        map(items => {
          if (!isCurrent) items = this.deDuplicateActivity(items);

          // filterint to remove meta comments
          items = items.filter(item => {
            if (item.type !== 'comment.created') return true;
            return !!item.data.object.text;
          });

          return items.map(item => {
            return {
              asset: item.context_asset as string,
              userOrGuestId: item.user?.['id'] || item.guest?.['id'],
              type: this.getType(item),
              user: item.user,
              guest: item.guest,
              private: !!item.data?.object?.private,
            };
          });
        })
      );
    });
  }

  private getRawTenantUserActivity(page: number, limit = activityPerPage) {
    const url = `${this.config.apiEndpoint}/customer_activities?limit=${limit}&page=${page}&sort[]=-created_at`;
    return this.http.get<{ data: CustomerActivityDto[] }>(url).pipe(
      tap(resp => {
        this.setUnseen(resp.data);
        resp.data.forEach(item => this.activityToUserActivityMap.set(item.activity['id'], item.id));
      }),
      switchMap(resp =>
        this.convertActivity(
          resp.data.map(item => ({ ...(item.activity as ActivityDto), id: item.id })),
          true
        )
      )
    );
  }

  public getTenantActivityWithUserActivity(page: number, limit = activityPerPage) {
    return forkJoin([
      this.getTenantActivity(page, limit),
      this.getRawTenantUserActivity(page, limit), // just to set unseen dots
    ]).pipe(
      map(([activity]) => {
        activity.days.forEach(day => {
          day.activityGroups.forEach(group => {
            group.items.forEach(item => {
              item.id = this.activityToUserActivityMap.get(item.id);
            });
          });
        });
        return activity;
      })
    );
  }

  private getRawGalleryUserActivity(galleryId: string, page: number, limit = activityPerPage) {
    const url = `${this.config.apiEndpoint}/galleries/${galleryId}/customer_activities?limit=${limit}&page=${page}&sort[]=-created_at`;
    return this.http.get<{ data: CustomerActivityDto[] }>(url).pipe(
      tap(resp => {
        this.setUnseen(resp.data);
        resp.data.forEach(item => this.activityToUserActivityMap.set(item.activity['id'], item.id));
      }),
      switchMap(resp =>
        this.convertActivity(
          resp.data.map(item => ({ ...(item.activity as ActivityDto), id: item.id })),
          true
        )
      )
    );
  }

  public getGalleryActivityWithUserActivity(galleryId: string, page: number, limit = activityPerPage) {
    return forkJoin([
      this.getGalleryActivity(galleryId, page, limit),
      this.getRawGalleryUserActivity(galleryId, page, limit), // just to set unseen dots
    ]).pipe(
      map(([activity]) => {
        activity.days.forEach(day => {
          day.activityGroups.forEach(group => {
            group.items.forEach(item => {
              item.id = this.activityToUserActivityMap.get(item.id);
            });
          });
        });
        return activity;
      })
    );
  }

  public getAssetActivityWithUserActivity(assetId: string, page: number, limit = activityPerPage) {
    return forkJoin([this.getAssetActivity(assetId, page, limit)]).pipe(
      map(([activity]) => {
        activity.days.forEach(day => {
          day.activityGroups.forEach(group => {
            group.items.forEach(item => {
              item.id = this.activityToUserActivityMap.get(item.id);
            });
          });
        });
        return activity;
      })
    );
  }

  private setUnseen(items: CustomerActivityDto[]) {
    items.forEach(item => {
      if (this.unseenActivityService.seenMap.has(item.id) && !this.unseenActivityService.seenMap.get(item.id)) return;
      this.unseenActivityService.seenMap.set(item.id, item.seen);
    });
  }

  private getUserActivity(url: string, outputContext: activityOutputContext, limit: number) {
    return this.http.get<{ data: CustomerActivityDto[] }>(url).pipe(
      map(resp => {
        this.setUnseen(resp.data);
        resp.data.forEach(item => (item.activity['id'] = item.id));
        return resp.data.map(item => item.activity as ActivityDto);
      }),
      switchMap(items => this.convertActivities(items, outputContext).pipe(map(days => ({ days, items })))),
      map(({ days, items }) => {
        return { days, isMore: items.length === limit };
      })
    );
  }

  private getActivity(url: string, outputContext: activityOutputContext, limit: number) {
    return this.http.get<{ data: ActivityDto[] }>(url).pipe(
      switchMap(resp =>
        this.convertActivities(resp.data, outputContext).pipe(map(days => ({ days, items: resp.data })))
      ),
      map(({ days, items }) => {
        return { days, isMore: items.length === limit };
      })
    );
  }

  private convertActivities(
    raw: (ActivityDto | ActivityExpandedAuthenticationPayloadDto)[],
    outputContext: activityOutputContext
  ): Observable<ActivityGroupDay[]> {
    return this.convertActivity(raw).pipe(
      map(items => this.splitActivityIntoDays(items)),
      switchMap(days =>
        forkJoinSafe(
          days.map(day =>
            this.groupActivities(day.activityItems, outputContext).pipe(
              map(activityGroups => {
                return {
                  name: day.name,
                  timestamp: day.timestamp,
                  activityGroups,
                };
              })
            )
          )
        )
      )
    );
  }

  private splitActivityIntoDays(activity: ConvertedActivity[]): ActivityDay[] {
    const days: ActivityDay[] = [];

    activity.forEach(item => {
      const date = new Date(item.time);
      // const ye = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date);
      // const mo = new Intl.DateTimeFormat('en', { month: 'short' }).format(date);
      // const da = new Intl.DateTimeFormat('en', { day: 'numeric' }).format(date);
      // let string = `${mo} ${da}, ${ye}`;
      const string = convertToGeneralRelativeTime(item.time);
      let day = days.find(d => d.name === string);
      if (!day) {
        days.push({ name: string, activityItems: [], timestamp: date.getTime() });
        day = days[days.length - 1];
      }

      day.activityItems.push(item);
    });

    days.sort((a, b) => (a.timestamp < b.timestamp ? 1 : -1));
    return days;
  }

  private groupActivities(
    items: ConvertedActivity[],
    outputContext: activityOutputContext
  ): Observable<ActivityGroup[]> {
    const groups: ActivityGroup[] = [];

    items.forEach(item => {
      let group = groups.find(g => this.fitsInGroup(g, item));
      if (!group) {
        group = {
          assetIds: [],
          assets: [],
          gallery: item.context.gallery,
          anonymousUserCount: 0,
          text: [],
          type: item.type,
          people: [],
          startTime: item.time,
          endTime: item.time,
          items: [],
          fragment: item.fragment,
          queryParams: item.queryParams,
          icon: this.getIcon(item.type, item.extraFields),
          iconColor: this.getIconColor(item.type, item.extraFields),
          grouping: undefined,
        };
        groups.push(group);
      } else {
        const grouping = <'people' | 'assets'>this.fitsInGroup(group, item);
        if (!group.grouping) group.grouping = grouping;
      }

      if (item.time > group.endTime) group.endTime = item.time;
      if (item.time < group.startTime) group.startTime = item.time;

      const assetIds = (item.context.assetId ? [item.context.assetId] : item.context.assetIds) || [];
      assetIds.forEach(id => {
        if (!group.assetIds.find(i => i === id)) group.assetIds.push(id);
      });

      if (item.context.person && !group.people.find(p => p.id === item.context.person.id)) {
        group.people.push(item.context.person);
      }

      if (!item.context.person) {
        if (!group.items.find(i => i.id === item.id)) {
          group.anonymousUserCount += 1;
        }
      }
      group.items.push(item);
    });

    return forkJoinSafe(
      groups.map(group => {
        if (group.assetIds.length === 0) return of(undefined);
        return forkJoinSafe(group.assetIds.slice(0, 5).map(id => this.getGroupAsset(id, group.gallery?.id))).pipe(
          tap(assets => {
            group.assets = assets;
          })
        );
      })
    ).pipe(
      map(() => {
        groups.forEach(group => {
          group.text = this.getText(group, outputContext);
          if (group.assets.length === 1) {
            if (group.gallery) {
              group.link = ['/a', 'gallery', group.gallery.id, 'designer', group.assets.find(a => a)?.id].filter(
                a => a
              );
            }
          }
          if (group.assets.length === 0 && outputContext === 'tenant') {
            if (group.gallery) {
              group.link = ['/a', 'gallery', group.gallery.id, 'designer'];
            }
          }
        });
        return groups.sort((a, b) => (a.endTime < b.endTime ? 1 : -1));
      })
    );
  }

  private getGroupAsset(id: string, galleryId: string): Observable<AssetDto> {
    if (!galleryId) return;
    return from(this.assetService.getAsset(id, galleryId, { noIncludes: true })).pipe(
      catchError(err => {
        if (!(err instanceof HttpErrorResponse)) return throwError(() => err);
        if (err.status !== 404 && err.status !== 403 && err.status !== 401) return throwError(() => err);
        return of(undefined);
      })
    );
  }

  private getDiffFromTimeRange(startTime: number, endTime: number, time: number) {
    if (time < endTime && time > startTime) return 0;
    if (time < startTime) return endTime - time;
    return time - startTime;
  }

  private fitsInGroup(group: ActivityGroup, item: ConvertedActivity): string | false {
    if (item.type !== group.type) return false;
    if (item.context.gallery) {
      if (item.context.gallery.id !== group.gallery?.id) return false;
    } else {
      if (item.context.deletedGalleryId !== group.deletedGalleryId) return false;
    }
    const timeDiff = this.getDiffFromTimeRange(group.startTime, group.endTime, item.time);
    const ago = Date.now() - item.time;

    // If the activity was less than 24 hours ago then we limit the time between activities
    // If the activity was more than 24 hours ago then we don't care about the time difference
    // Note: We don't care about the time difference because the grouping is already split per day.
    if (group.type === 'gallery-view.created') {
      // for gallery views group everything that happened more than 2 hours ago
      if (ago < 2 * 60 * 60 * 1000 && timeDiff > biggestGroupTimeDiff) return false;
    } else {
      if (ago < 24 * 60 * 60 * 1000 && timeDiff > biggestGroupTimeDiff) return false;
    }

    const grouping = groupingFields[group.type].find(field => {
      if (group.items.find(i => i.id === item.id)) return true;

      // if this groups type can be grouped by assets (Same person, multiple assets)
      // we check to make sure this group doesnt already contain multiple people
      if (field === 'assets') {
        if (group.grouping && group.grouping !== 'assets') return false;
        if (group.people.length > 1) return false;
        if (group.people[0] && item.context.person) return group.people[0].id === item.context.person?.id;
        return false;
      }
      // if this groups type can be grouped by people (Same asset, multiple people)
      // we check to make sure this group doesnt already contain multiple assets
      else if (field === 'people') {
        if (group.grouping && group.grouping !== 'people') return false;
        // Items that have been seen can't be combined with items that have not been seen
        const map = this.unseenActivityService.seenMap;
        if (map.get(group.items[0].id) !== map.get(item.id)) return false;

        if (group.assetIds.length > 1) return false;

        // Anonymous and named people cant be in the same group ("Nathan and 3 others and 2 guests" doesnt work)
        if (group.anonymousUserCount > 0 && item.context.person) return false;
        if (group.people.length > 0 && !item.context.person) return false;

        // For certain types the assets are not relevant
        if (['gallery-download.created', 'gallery-view.created', 'gallery.status-updated'].includes(group.type))
          return true;

        // Otherwise, we only allow the grouping if the asset is the same as the one in the group
        if (group.assetIds.length === 1 && item.context.assetId) return group.assetIds[0] === item.context.assetId;

        return false;
      }
    });

    return grouping;
  }

  private convertActivity(raw: (ActivityDto | ActivityExpandedAuthenticationPayloadDto)[], simple = false) {
    if (simple) return of(this.convertActivityWithGalleries(raw, []));
    return this.getGalleries(raw).pipe(map(galleries => this.convertActivityWithGalleries(raw, galleries)));
  }

  private convertActivityWithGalleries(
    raw: (ActivityDto | ActivityExpandedAuthenticationPayloadDto)[],
    galleries: GalleryDto[]
  ): ConvertedActivity[] {
    const activityItems: ConvertedActivity[] = [];

    raw.forEach(item => {
      const type = this.getType(item);
      if (!type) return;
      const gallery = galleries.find(g => g.id === item.context_gallery);

      const user = <DisplayUser>item.user;
      const guest = <DefaultDisplayGuestDto>item.guest;
      let person: ActivityGroupPerson;
      if (user || guest) {
        const name = this.calcName(user || guest);
        const initials = this.getInitials(user || guest);

        person = {
          type: item.user ? 'user' : 'guest',
          name,
          initials,
          id: user ? user.id : guest.id,
          avatar_url: user ? user.avatar_url : undefined,
          anonymous_id: guest ? guest.anonymous_id : undefined,
        };
      }

      let fragment: string;
      let queryParams: { [key: string]: string };
      if (['asset-annotation.created', 'comment.created'].includes(type)) {
        fragment = 'comments';
        queryParams = { comment: item.data.object.parent_comment || item.data.object.id };
      }

      let assetIds: string[];
      if (item.type === 'archive.created') {
        const archive = <ArchiveDto>item.data.object;
        if (archive.zip_parameters[0]?.items) {
          assetIds = archive.zip_parameters[0]?.items;
        }
      }

      const activityItem: ConvertedActivity = {
        id: item['id'],
        time: item['created_at'] * 1000,
        context: {
          gallery,
          assetId: <string>item.context_asset,
          assetIds,
          person,
        },
        type,
        rawType: item.type,
        fragment,
        queryParams,
        extraFields: this.extraFields(item),
      };
      activityItems.push(activityItem);
    });

    this.unseenActivityService.registerNewActivityItems(activityItems);

    return activityItems;
  }

  private extraFields(item: ActivityDto | ActivityExpandedAuthenticationPayloadDto) {
    if (item.type === 'gallery.status-updated') {
      return {
        status: item.data.object['status'],
      };
    }
    return {};
  }

  private getFirstName(userOrGuest: DefaultDisplayGuestDto | DisplayUser) {
    if ('first_name' in userOrGuest) return userOrGuest.first_name;
    const { first_name } = splitName(userOrGuest.name);
    return first_name;
  }

  private getLastName(userOrGuest: DefaultDisplayGuestDto | DisplayUser) {
    if ('last_name' in userOrGuest) return userOrGuest.last_name;
    const { last_name } = splitName(userOrGuest.name);
    return last_name;
  }

  private calcName(userOrGuest: DefaultDisplayGuestDto | DisplayUser) {
    if ('name' in userOrGuest) return userOrGuest.name;
    if (userOrGuest.last_name) return `${userOrGuest.first_name} ${userOrGuest.last_name}`;
    return userOrGuest.first_name;
  }

  private getInitials(userOrGuest: DefaultDisplayGuestDto | DisplayUser): string {
    const first_name = this.getFirstName(userOrGuest);
    const last_name = this.getLastName(userOrGuest);
    if (last_name) return `${first_name[0]}${last_name[0]}`;
    return `${first_name[0]}${first_name[1]}`;
  }

  private isGalleryActivity(group: ActivityGroup) {
    return [
      'gallery-download.created',
      'gallery-view.created',
      'multi-asset-download.created',
      'gallery.status-updated',
    ].includes(group.type);
  }

  /**
   * Gets the text for a group.
   * The text is broken down into 3/4 parts
   * PEOPLE - ACTION - (ASSETS) - (GALLERY)
   */
  private getText(group: ActivityGroup, outputContext: activityOutputContext): GroupTextItem[] {
    return [
      this.getPeopleGroupTextItem(group),
      this.getActionGroupTextItem(group, outputContext),
      this.getAssetsGroupTextItem(group, outputContext),
      this.getGalleryPreGroupTextItem(group, outputContext),
      this.getGalleryGroupTextItem(group, outputContext),
    ].filter(a => a?.content);
  }

  private getPeopleGroupTextItem(group: ActivityGroup): GroupTextItem {
    let text: string;
    if (group.people.length === 1) text = `${group.people[0].name} `;
    else if (group.people.length === 2) text = `${group.people[0].name} and ${group.people[1].name} `;
    else if (!group.anonymousUserCount) {
      const others = group.people.length - 2;
      if (others === 1) text = `${group.people[0].name}, ${group.people[1].name}, and ${others} other `;
      if (others > 1) text = `${group.people[0].name}, ${group.people[1].name}, and ${others} others `;
    } else {
      if (group.anonymousUserCount === 1) text = `A Visitor `;
      else text = `${group.anonymousUserCount} Visitors `;
    }
    return { content: text, type: 'people' };
  }

  private getActionGroupTextItem(group: ActivityGroup, outputContext: activityOutputContext): GroupTextItem {
    const word = this.getWord(group.type, outputContext);
    if (word) return { content: ` ${word} `, type: 'text' };
    console.warn('Unhandled group type', group);
  }

  private getAssetsGroupTextItem(group: ActivityGroup, outputContext: activityOutputContext): GroupTextItem {
    let text: string;
    if (group.assetIds.length === 1 && group.type !== 'multi-asset-download.created' && outputContext !== 'asset') {
      const asset = group.assets.find(a => a);
      if (asset) text = `${this.shortenAssetName(asset.version_data.asset_name, outputContext)}`;
      else text = 'a deleted asset';
    } else if (group.assetIds.length > 1) text = `${group.assetIds.length} assets`;
    return { content: text, type: 'assets' };
  }

  private getGalleryPreGroupTextItem(group: ActivityGroup, outputContext: activityOutputContext): GroupTextItem {
    let text: string;
    if (['gallery-download.created', 'gallery-view.created', 'gallery.status-updated'].includes(group.type)) text = '';
    else {
      if (outputContext === 'gallery') {
        if (this.isGalleryActivity(group)) text = ' in ';
      } else {
        if (group.assetIds.length > 1) text = ` in `;
      }
    }
    return { content: text, type: 'text', subType: 'gallery' };
  }

  private getGalleryGroupTextItem(group: ActivityGroup, outputContext: activityOutputContext): GroupTextItem {
    let text: string;
    if (outputContext === 'gallery') {
      if (this.isGalleryActivity(group)) text = 'this gallery';
      return { content: text, type: 'text' };
    } else {
      if (this.isGalleryActivity(group) || group.assetIds.length > 1) {
        if (group.gallery) text = group.gallery.name || group.gallery.title || 'Untitled';
        else text = 'a deleted gallery';
      }
      return { content: text, type: 'gallery' };
    }
  }

  private getGalleries(activity: (ActivityDto | ActivityExpandedAuthenticationPayloadDto)[]): Observable<GalleryDto[]> {
    const ids: Set<string> = new Set(activity.map(a => <string>a.context_gallery));

    const requests = Array.from(ids).map(id => from(this.galleryService.getGallery(id)).pipe(catchError(() => EMPTY)));

    if (!requests.length) return of([]);

    return forkJoin(requests).pipe(map((galleries: GalleryDto[]) => galleries.filter(Boolean)));
  }

  private getType(item: ActivityDto | ActivityExpandedAuthenticationPayloadDto): activityType {
    if (item.type === 'asset-like.created') return 'asset.liked';
    if (item.type === 'asset-status.created') {
      if (!item.data.object) {
        console.error('Invalid activity (no object): ' + item['id']);
        return;
      }
      return `asset.status_${item.data.object['status'] as AssetStatus}`;
    }
    if (item.type === 'asset-color-label.created') {
      if (!item.data.object) {
        console.error('Invalid activity (no object): ' + item['id']);
        return;
      }
      const to = item.data.object['color_label'];
      if (to) {
        return <activityType>('asset.color_label_' + to);
      }
      return 'asset.removed_color_label';
    }
    if (item.type === 'comment.created') {
      return 'comment.created';
    }
    if (item.type === 'archive.created' || (item.type as string) === 'asset-download.created') {
      if ((item.data.object as ArchiveDto).zip_parameters[0]?.items) {
        return 'multi-asset-download.created';
      }
      return 'gallery-download.created';
    }
    if (item.type === 'single-asset-download.created') {
      return 'asset-download.created';
    }
    if (item.type === 'asset-annotation.created') {
      return 'asset-annotation.created';
    }
    if (item.type === 'gallery-view.created') {
      return 'gallery-view.created';
    }
    if (item.type === 'gallery.status-updated') {
      return 'gallery.status-updated';
    }
    console.warn('activity type not handled', item.type, item);
  }

  private getIcon(type: activityType, extraFields: { [key: string]: unknown }): string {
    if (type === 'gallery-view.created') return '/assets/icons/eye-open.svg';
    if (type === 'gallery-download.created') return '/assets/icons/down-circle.svg';
    if (type === 'asset-download.created') return '/assets/icons/down-circle.svg';
    if (type === 'multi-asset-download.created') return '/assets/icons/down-circle.svg';
    if (type === 'asset.liked') return '/assets/icons/heart-filled.svg';
    if (type === 'asset.approved') return '/assets/icons/flag-check.svg'; // old
    if (type === 'asset.rejected') return '/assets/icons/flag-cross.svg'; // old
    if (type === 'asset.unapproved') return '/assets/icons/flag-dotted.svg'; // old
    if (type === 'asset.unrejected') return '/assets/icons/flag-dotted.svg'; // old
    if (type.startsWith('asset.status_'))
      return `/assets/icons/${getAssetStatusIcon(type.split('asset.status_')[1] as AssetStatus)}.svg`;
    if (type.startsWith('asset.color_label')) return '/assets/icons/color-label-filled.svg';
    if (type === 'asset.removed_color_label') return '/assets/icons/color-label-dotted.svg';
    if (type === 'comment.created') return '/assets/icons/comment-bubble.svg';
    if (type === 'asset-annotation.created') return '/assets/icons/annotate-brush.svg';
    if (type === 'gallery.status-updated') {
      const icon = getGalleryStatusIcon(extraFields.status as GalleryStatus);
      return `/assets/icons/${icon}.svg`;
    }
    console.warn('No icon found for activity type');
  }

  private getIconColor(type: activityType, extraFields: { [key: string]: unknown }): string {
    if (type === 'gallery-view.created') return 'grey-10';
    if (type === 'gallery-download.created') return 'grey-50';
    if (type === 'asset-download.created') return 'grey-50';
    if (type === 'multi-asset-download.created') return 'grey-50';
    if (type === 'asset.liked') return 'warn';
    if (type === 'asset.approved') return 'green'; // old
    if (type === 'asset.rejected') return 'warn'; // old
    if (type === 'asset.unapproved') return 'grey-10'; // old
    if (type === 'asset.unrejected') return 'grey-10'; // old
    if (type.startsWith('asset.status_'))
      return getAssetStatusColor(type.split('asset.status_')[1] as AssetStatus) || 'grey-10';
    if (type === 'asset.color_label_red') return 'red';
    if (type === 'asset.color_label_orange') return 'orange';
    if (type === 'asset.color_label_blue') return 'blue';
    if (type === 'asset.color_label_purple') return 'purple';
    if (type === 'asset.color_label_green') return 'green';
    if (type === 'asset.removed_color_label') return 'grey-10';
    if (type === 'comment.created') return 'primary';
    if (type === 'asset-annotation.created') return 'gradient';
    if (type === 'gallery.status-updated') {
      return getGalleryStatusColor(extraFields.status as GalleryStatus);
    }
    console.warn('No icon color for activity type');
  }

  public getWord(type: activityType, outputContext: activityOutputContext): string {
    if (type === 'gallery-view.created') return 'viewed';
    if (type === 'gallery-download.created') return 'downloaded';
    if (type === 'asset-download.created') return 'downloaded';
    if (type === 'multi-asset-download.created') return 'downloaded';
    if (type === 'asset.liked') return 'favorited';
    if (type === 'asset.approved') return 'approved'; // old
    if (type === 'asset.rejected') return 'rejected'; // old
    if (type === 'asset.unapproved') return 'un-approved'; // old
    if (type === 'asset.unrejected') return 'un-rejected'; // old
    if (type.startsWith('asset.status_')) return outputContext === 'asset' ? `changed status` : `changed status of`;
    if (type.startsWith('asset.color_label')) return 'labeled';
    if (type === 'asset.removed_color_label')
      return outputContext === 'asset' ? 'removed a color label' : 'removed a color label from';
    if (type === 'comment.created') return outputContext === 'asset' ? 'commented' : 'commented on';
    if (type === 'asset-annotation.created') return outputContext === 'asset' ? 'annotated' : 'annotated on';
    if (type === 'gallery.status-updated')
      return outputContext === 'asset' ? 'updated the status' : 'updated the status of';
    console.warn('No icon color for activity type');
  }

  private shortenAssetName(name: string, context: activityOutputContext) {
    let maxLength = context === 'gallery' ? 18 : 40;
    if (ScreenSizes.screenSize() === 'mobile') maxLength = 18;
    if (!name || name.length <= maxLength) return name;
    const half = Math.floor((maxLength - 4) / 2);
    return `${name.substring(0, half)}...${name.substring(name.length - half, name.length)}`;
  }

  private getPassword(galleryId: string) {
    const password = this.galleryPasswordService.getPassword(galleryId);
    if (!password) return '';
    return '&password=' + encodeURIComponent(password);
  }

  private deDuplicateActivity(items: ActivityDto[]): ActivityDto[] {
    const filtered: ActivityDto[] = [];
    items.forEach(activity => {
      const activityPersonId = activity.user?.['id'] ?? activity.guest?.['id'];

      const duplicate = filtered.find(a => {
        if (a.context_asset !== activity.context_asset) return false;
        if (['asset-flag.created', 'asset-color-label.created', 'asset-status.created'].includes(activity.type)) {
          return a.type === activity.type;
        }

        const aPersonId = a.user?.['id'] ?? a.guest?.['id'];
        if (aPersonId !== activityPersonId) return false;
        return activity.type.split('.')[0] === a.type.split('.')[0];
      });
      if (duplicate) return;
      filtered.push(activity);
    });

    return filtered.filter(activity => {
      if (activity.type === 'asset-status.created') {
        return activity.data.object['status'] !== 'no_status';
      }
      if (activity.type === 'asset-color-label.created') {
        return activity.data.object['color_label'] !== null;
      }
      if (activity.type.split('.')[1] === 'deleted') {
        return false;
      }
      return true;
    });
  }
}
